import type { ReactNode } from "react";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

type EditorWindowProps = {
  filename: string;
  code: string;
  className?: string;
  language?: string;
  highlight?: boolean;
};

type TokenKind =
  | "plain"
  | "keyword"
  | "string"
  | "number"
  | "comment"
  | "jsxTag"
  | "jsxAttr"
  | "identifier";

type Token = { kind: TokenKind; value: string };

const KEYWORDS = new Set([
  "import",
  "from",
  "export",
  "default",
  "function",
  "return",
  "const",
  "let",
  "var",
  "type",
  "interface",
  "as",
  "if",
  "else",
  "for",
  "while",
  "new",
  "try",
  "catch",
  "finally",
  "await",
  "async",
]);

function styleFor(kind: TokenKind) {
  switch (kind) {
    case "keyword":
      return "text-fuchsia-200";
    case "string":
      return "text-emerald-200";
    case "number":
      return "text-amber-200";
    case "comment":
      return "text-zinc-400/80";
    case "jsxTag":
      return "text-violet-200";
    case "jsxAttr":
      return "text-sky-200/90";
    case "identifier":
      return "text-cyan-100/90";
    default:
      return "text-zinc-100/90";
  }
}

function tokenizeCode(code: string): Token[] {
  // NOTE: lightweight TSX-ish tokenization (not a full parser)
  const out: Token[] = [];

  // split // comment (ignore URLs: keep it simple by requiring space before //)
  const commentIdx = code.search(/(^|[\s])\/\//);
  const codePart = commentIdx >= 0 ? code.slice(0, commentIdx === 0 ? 0 : commentIdx + 1) : code;
  const commentPart = commentIdx >= 0 ? code.slice(commentIdx === 0 ? 0 : commentIdx + 1) : "";

  const re =
    /("([^"\\]|\\.)*")|('([^'\\]|\\.)*')|(`([^`\\]|\\.)*`)|(<\/?[A-Za-z][\w:-]*)|(\/?>)|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_$][\w$]*\b)(?=\s*=)|(\b[A-Za-z_$][\w$]*\b)/g;

  let last = 0;
  for (const m of codePart.matchAll(re)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push({ kind: "plain", value: codePart.slice(last, idx) });

    const raw = m[0] ?? "";
    let kind: TokenKind = "plain";

    if (m[1] || m[3] || m[5]) kind = "string";
    else if (m[7]) kind = "jsxTag";
    else if (m[8]) kind = "jsxTag";
    else if (m[9]) kind = "number";
    else if (m[10]) kind = "jsxAttr";
    else if (m[11]) kind = KEYWORDS.has(raw) ? "keyword" : "identifier";

    out.push({ kind, value: raw });
    last = idx + raw.length;
  }
  if (last < codePart.length) out.push({ kind: "plain", value: codePart.slice(last) });

  if (commentPart) out.push({ kind: "comment", value: commentPart });
  return out.length ? out : [{ kind: "plain", value: code }];
}

function renderHighlightedLine(line: string): ReactNode {
  const tokens = tokenizeCode(line);
  return tokens.map((t, i) => (
    <span key={i} className={styleFor(t.kind)}>
      {t.value}
    </span>
  ));
}

export function EditorWindow({
  filename,
  code,
  className,
  language = "TSX",
  highlight = true,
}: EditorWindowProps) {
  const lines = useMemo(() => code.replace(/\n$/, "").split("\n"), [code]);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border/60",
        "bg-[linear-gradient(180deg,rgba(24,24,27,0.92)_0%,rgba(9,9,11,0.96)_100%)]",
        "shadow-[0_16px_50px_rgba(0,0,0,0.18)]",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-zinc-900/60 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          <span className="ml-3 font-mono text-xs text-zinc-300/85">{filename}</span>
        </div>
        <span className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 font-mono text-[11px] text-zinc-200/80">
          {language}
        </span>
      </div>

      <div className="overflow-x-auto">
        <pre className="m-0 min-w-full p-0 font-mono text-[13px] leading-relaxed text-zinc-100/90">
          {lines.map((line, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[3.25rem_1fr] items-start gap-0 border-b border-white/0 px-0 hover:bg-white/[0.04]"
            >
              <span className="select-none px-3 py-0.5 text-right text-zinc-500/90">
                {idx + 1}
              </span>
              <code className="whitespace-pre px-3 py-0.5 text-zinc-100/90">
                {line.length ? (highlight ? renderHighlightedLine(line) : line) : " "}
              </code>
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}

